import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Clock, RefreshCw } from "lucide-react";
import apiClient from "@/lib/api";
import { PAYMENT_STATUS_LABELS, formatRupiah } from "@/components/booking/bookingMeta";

export default function PaymentReturnPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orderId = params.get("order");
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (!orderId) { setError(true); setLoading(false); return; }
    setLoading(true);
    setError(false);
    try {
      // returnUrl is UX-only; the real status comes from the backend (callback source of truth).
      const { data } = await apiClient.get(`/payments/by-order/${orderId}`);
      setPayment(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const st = payment?.status;
  const icon = st === "paid" ? <CheckCircle2 className="text-emerald-600" size={40} />
    : st === "failed" || st === "expired" || st === "cancelled" ? <XCircle className="text-rose-600" size={40} />
    : <Clock className="text-amber-600" size={40} />;

  return (
    <div className="max-w-md mx-auto px-4 py-16" data-testid="payment-return-page">
      <div className="bg-card border border-border rounded-2xl p-8 text-center">
        {loading ? (
          <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-primary" size={30} /></div>
        ) : error ? (
          <div data-testid="payment-return-error">
            <XCircle className="text-rose-600 mx-auto" size={40} />
            <h1 className="mt-4 font-heading text-2xl font-bold">Status tidak ditemukan</h1>
            <p className="mt-2 text-sm text-muted-foreground">Kami tidak dapat menemukan pembayaran ini. Cek daftar booking kamu.</p>
          </div>
        ) : (
          <div data-testid="payment-return-status">
            <div className="mx-auto grid place-items-center w-16 h-16 rounded-2xl bg-secondary mb-2">{icon}</div>
            <p className="text-xs text-muted-foreground">Pembayaran sedang diproses oleh sistem</p>
            <h1 className="mt-2 font-heading text-2xl font-bold" data-testid="payment-return-status-label">
              {PAYMENT_STATUS_LABELS[st] || st}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {payment?.property_name} · Kamar {payment?.room_number}
            </p>
            <p className="mt-1 font-heading text-xl font-bold text-primary">{formatRupiah(payment?.amount)}</p>
            {st === "pending" && (
              <button onClick={load} data-testid="payment-return-refresh"
                className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-primary border border-border hover:bg-secondary/60 transition-colors">
                <RefreshCw size={14} /> Perbarui Status
              </button>
            )}
          </div>
        )}
        <button onClick={() => navigate("/customer/bookings")} data-testid="payment-return-to-bookings"
          className="mt-6 w-full py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform">
          Lihat Booking Saya
        </button>
      </div>
    </div>
  );
}
